
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/AdminSidebar";
import { CodeBlock } from "@/components/api-docs/CodeBlock";
import { FileCode, Code, ServerCog } from "lucide-react";
import { SubscriptionStatusCheck } from "@/components/payment/SubscriptionStatusCheck";

const SubscriptionStatusWebhook = () => {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-100">
        <AdminSidebar />
        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="mb-12">
              <h1 className="text-4xl font-bold" style={{ color: "#008169" }}>
                Subscription Status API
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                API endpoint for checking subscription status
              </p>
            </div>

            <div className="space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold">Overview</h2>
                <p className="text-gray-700">
                  The Subscription Status API allows external applications to check 
                  if a school has an active subscription for a specific product.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold">Authentication</h2>
                <p className="text-gray-700">
                  All API requests require an API key that should be included in the request header:
                </p>
                <CodeBlock
                  language="http"
                  code={`x-api-key: YOUR_API_KEY`}
                  title="API Key Header"
                />
                <p className="text-gray-700 mt-4">
                  <strong>Note:</strong> To generate or manage API keys, please contact your account administrator.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold">Check Subscription Status</h2>
                <p className="text-gray-700">
                  Check if a school has an active subscription for a specific product.
                </p>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Endpoint</h3>
                  <CodeBlock
                    language="http"
                    code={`POST https://swhwykzorcbiutczhjgn.functions.supabase.co/check-subscription-status`}
                    title="Endpoint"
                  />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Request</h3>
                  <CodeBlock
                    language="json"
                    code={`{
  "productId": "550e8400-e29b-41d4-a716-446655440000",
  "schoolId": "12345"
}`}
                    title="Request Body"
                  />
                  
                  <p className="text-gray-700">
                    <strong>Parameters:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>
                      <code className="bg-gray-100 text-red-600 p-1 rounded">productId</code> 
                      <span className="text-sm ml-2">(required)</span> - The product's unique ID
                    </li>
                    <li>
                      <code className="bg-gray-100 text-red-600 p-1 rounded">schoolId</code> 
                      <span className="text-sm ml-2">(required)</span> - The school's unique ID
                    </li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Response</h3>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "active": true,
  "status": "active",
  "subscription_id": "b5d1b01d-9e99-4c09-b0b3-8f5248c6e48a",
  "end_date": "2023-11-14T08:30:00.000Z",
  "token": "abc123def456ghi789"
}`}
                    title="Success Response - Active Subscription (200 OK)"
                  />
                  
                  <CodeBlock
                    language="json"
                    code={`{
  "success": true,
  "active": false,
  "message": "No active subscription found for the specified product and school",
  "token": "abc123def456ghi789"
}`}
                    title="Success Response - No Active Subscription (200 OK)"
                  />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Error Responses</h3>
                  <CodeBlock
                    language="json"
                    code={`{
  "success": false,
  "error": "Missing required parameters: productId and schoolId"
}`}
                    title="Error Response (400 Bad Request)"
                  />
                  
                  <CodeBlock
                    language="json"
                    code={`{
  "success": false,
  "error": "Invalid API key"
}`}
                    title="Error Response (401 Unauthorized)"
                  />
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold mb-4">Test Subscription Status</h2>
                <SubscriptionStatusCheck />
              </section>

              <section className="mt-6">
                <h3 className="text-xl font-bold mb-4">Related APIs</h3>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <h4 className="text-lg font-semibold">Pricing API</h4>
                    <p className="text-gray-600">
                      Get pricing information for products and plans.
                    </p>
                    <a
                      href="/pricing-api-docs"
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      <FileCode className="h-4 w-4 mr-1" />
                      View Pricing API Documentation
                    </a>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <h4 className="text-lg font-semibold">Payment API</h4>
                    <p className="text-gray-600">
                      Handle payment status updates and notifications.
                    </p>
                    <a
                      href="/admin/billing-webhook"
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      <Code className="h-4 w-4 mr-1" />
                      View Payment API Documentation
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SubscriptionStatusWebhook;
